<template>
  <div class="pp-tab-surface">
    <div class="pp-avail-head">
      <div class="stats">
        <div class="s">
          <span class="l">{{ t('property.v2Overview.hilites.totalUnits') }}</span>
          <span class="v">{{ totalUnits }}</span>
        </div>
        <div class="s">
          <span class="l">{{ t('property.v2Availability.available') }}</span>
          <span class="v green">{{ availableUnits }}</span>
        </div>
        <div
          v-if="exclusiveUnits > 0"
          class="s"
        >
          <span class="l">{{ t('property.v2Availability.exclusiveToInsiders') }}</span>
          <span class="v gold">{{ exclusiveUnits }}</span>
        </div>
        <div
          v-if="updatedLabel"
          class="s"
        >
          <span class="l">{{ t('property.v2Availability.lastSync') }}</span>
          <span class="v small">{{ updatedLabel }}</span>
        </div>
      </div>
      <div
        v-if="filters.length > 1"
        class="pp-avail-filters"
      >
        <button
          v-for="f in filters"
          :key="f.id"
          type="button"
          class="f"
          :class="{ on: activeFilter === f.id }"
          @click="activeFilter = f.id"
        >
          {{ f.label }}
        </button>
      </div>
    </div>

    <div
      v-if="filteredRows.length"
      class="pp-avail-table"
    >
      <div class="pp-avail-row h">
        <div>{{ t('property.v2Availability.headers.unit') }}</div>
        <div>{{ t('property.v2Availability.headers.layout') }}</div>
        <div>{{ t('property.v2Availability.headers.view') }}</div>
        <div>{{ t('property.v2Availability.headers.floor') }}</div>
        <div>{{ t('property.v2Availability.headers.area') }}</div>
        <div>{{ t('property.v2Availability.headers.price') }}</div>
        <div>{{ t('property.v2Availability.headers.paymentPlan') }}</div>
        <div>{{ t('property.v2Availability.headers.status') }}</div>
        <div></div>
      </div>
      <div
        v-for="row in filteredRows"
        :key="row.id"
        class="pp-avail-row"
        :class="{ sold: row.statusKey === 'sold' }"
      >
        <div class="u">
          <button
            v-if="row.catalogueUnitId !== null"
            type="button"
            class="u-link"
            :aria-label="t('property.v2Availability.openUnitAriaLabel', { unit: row.unitCode })"
            @click="$emit('unit-select', row)"
          >
            {{ row.unitCode }}
          </button>
          <template v-else>{{ row.unitCode }}</template>
        </div>
        <div class="layout">
          <button
            v-if="row.floorPlanUrls.length"
            type="button"
            class="layout-thumb"
            :aria-label="t('property.v2Availability.viewFloorPlanAriaLabel')"
            @click="openFloorPlan(row.floorPlanUrls)"
          >
            <img
              :src="row.floorPlanUrls[0]"
              alt=""
              loading="lazy"
              @error="($event.target as HTMLImageElement).style.display = 'none'"
            />
            <span
              v-if="row.floorPlanUrls.length > 1"
              class="layout-count"
              >{{ row.floorPlanUrls.length }}</span
            >
          </button>
          <span class="layout-label">{{ row.layout }}</span>
        </div>
        <div class="view">{{ row.view || '—' }}</div>
        <div class="fl">{{ row.floor || '—' }}</div>
        <div class="area">
          {{ row.area }}
          <span class="sub">{{ areaSuffix }}</span>
        </div>
        <div class="price">
          <span class="cur">{{ row.currency }}</span>
          {{ row.priceFormatted }}
        </div>
        <div class="pp">{{ row.paymentPlan || '—' }}</div>
        <div>
          <span
            class="st"
            :class="row.statusKey"
            >{{ row.statusLabel }}</span
          >
        </div>
        <div>
          <slot
            name="unit-action"
            :row="row"
          >
            <button
              v-if="row.statusKey === 'sold'"
              type="button"
              class="book dis"
              disabled
            >
              —
            </button>
            <button
              v-else
              type="button"
              class="book"
              :class="{ gold: row.statusKey === 'excl' }"
              @click="$emit('book', row.id)"
            >
              {{ t('property.v2Availability.book') }}
              <span
                class="inline-block rtl:-scale-x-100"
                aria-hidden="true"
                >→</span
              >
            </button>
          </slot>
        </div>
      </div>
    </div>

    <div
      v-else
      class="empty"
    >
      <p>{{ t('property.v2Availability.empty') }}</p>
    </div>

    <PropertyImageLightbox
      v-if="lightbox.length"
      :images="lightbox"
      @close="lightbox = []"
    />
  </div>
</template>

<script setup lang="ts">
import { format, formatDistanceToNowStrict } from 'date-fns';
import type { PropertyDetailed, Apartment } from '@/types/properties';
import { convertCurrency } from '@/helpers/currency.helper';

const props = defineProps<{
  item: PropertyDetailed;
}>();

defineEmits<{
  (e: 'book', id: string): void;
  // 100% Commission rows: open the unit popup (layout, payment plan, sales offer, booking).
  (e: 'unit-select', row: { catalogueUnitId: number | null; statusKey: string }): void;
}>();

const { t, locale } = useI18n();
const { formatNumber, formatRelativeTime, formatDate } = useFormat();
const preferences = usePreferencesStore();
const { areaSuffix } = useAreaFormat();

type StatusKey = 'avail' | 'hold' | 'sold' | 'resv' | 'book' | 'excl';

type AvailabilityRow = {
  id: string;
  // Catalogue unit id (100% Commission units) — the page's hold action keys on it.
  catalogueUnitId: number | null;
  unitCode: string;
  layout: string;
  floorPlanUrls: string[];
  paymentPlan: string | null;
  view: string;
  floor: string;
  area: string;
  priceFormatted: string;
  currency: string;
  statusKey: StatusKey;
  statusLabel: string;
};

type Row = AvailabilityRow;

const decimalFormatter = { format: (v: number) => formatNumber(v, { maximumFractionDigits: 0 }) };

const apartments = computed<Apartment[]>(() => props.item?.apartments ?? []);

const mapStatus = (apartment: Apartment): { key: StatusKey; label: string } => {
  const raw = String((apartment as { status?: string }).status || '').toLowerCase();
  const exclusive = !!(apartment as { exclusive?: boolean | number }).exclusive;
  if (exclusive) return { key: 'excl', label: `★ ${t('property.v2Availability.statusLabels.exclusive')}` };
  if (raw.includes('sold')) return { key: 'sold', label: t('property.v2Availability.statusLabels.sold') };
  if (raw.includes('reserv')) return { key: 'resv', label: t('property.v2Availability.statusLabels.reserved') };
  if (raw.includes('book')) return { key: 'book', label: t('property.v2Availability.statusLabels.booked') };
  if (raw.includes('hold')) return { key: 'hold', label: t('property.v2Availability.statusLabels.onHold') };
  return { key: 'avail', label: t('property.v2Availability.available') };
};

const formatPrice = (raw: number | string | undefined, sourceCurrency: string) => {
  const num = Number(raw) || 0;
  if (!num) return '—';
  return decimalFormatter.format(convertCurrency(num, sourceCurrency, preferences.currency));
};

const rows = computed<Row[]>(() => {
  return apartments.value.map((apt, idx) => {
    const status = mapStatus(apt);
    const sourceCurrency = apt.price?.currency || props.item?.price?.currency || 'AED';
    const a = apt as {
      number?: string | number;
      title?: { en?: string };
      view?: { en?: string };
      floor?: number | string;
      area_min?: { m2?: number | string; ft2?: number | string };
      rooms_count?: { en?: string };
      floorPlanUrl?: string | null;
      floorPlanUrls?: string[];
      paymentPlan?: string | null;
      catalogueUnitId?: number;
    };
    const code = String(a.number || a.title?.en || `${idx + 1}`);
    const layout = a.rooms_count?.en || a.title?.en || '—';
    const view = a.view?.en || '';
    const floor = a.floor ? String(a.floor) : '';
    const areaRaw = preferences.measureUnit === 'ft2' ? a.area_min?.ft2 : a.area_min?.m2;
    const area = areaRaw ? decimalFormatter.format(Number(areaRaw)) : '—';
    return {
      id: `${code}-${idx}`,
      catalogueUnitId: typeof a.catalogueUnitId === 'number' ? a.catalogueUnitId : null,
      unitCode: code,
      layout,
      floorPlanUrls: a.floorPlanUrls?.length ? a.floorPlanUrls : a.floorPlanUrl ? [a.floorPlanUrl] : [],
      paymentPlan: a.paymentPlan ?? null,
      view,
      floor,
      area,
      priceFormatted: formatPrice(apt.price?.min, sourceCurrency),
      currency: preferences.currency,
      statusKey: status.key,
      statusLabel: status.label,
    };
  });
});

const totalUnits = computed(() => apartments.value.length);
const availableUnits = computed(
  () => rows.value.filter((r) => r.statusKey === 'avail' || r.statusKey === 'excl').length,
);
const exclusiveUnits = computed(() => rows.value.filter((r) => r.statusKey === 'excl').length);

const filters = computed(() => {
  const buckets: Array<{ id: 'all' | StatusKey; label: string }> = [
    { id: 'all', label: t('property.v2Availability.filters.all', { count: totalUnits.value }) },
  ];
  const counts: Record<StatusKey, number> = { avail: 0, hold: 0, sold: 0, resv: 0, book: 0, excl: 0 };
  rows.value.forEach((r) => {
    counts[r.statusKey]++;
  });
  if (counts.avail)
    buckets.push({ id: 'avail', label: t('property.v2Availability.filters.available', { count: counts.avail }) });
  if (counts.hold)
    buckets.push({ id: 'hold', label: t('property.v2Availability.filters.hold', { count: counts.hold }) });
  if (counts.resv)
    buckets.push({ id: 'resv', label: t('property.v2Availability.filters.reserved', { count: counts.resv }) });
  if (counts.book)
    buckets.push({ id: 'book', label: t('property.v2Availability.filters.booked', { count: counts.book }) });
  if (counts.sold)
    buckets.push({ id: 'sold', label: t('property.v2Availability.filters.sold', { count: counts.sold }) });
  return buckets;
});

const activeFilter = ref<'all' | StatusKey>('all');

const lightbox = ref<string[]>([]);
const openFloorPlan = (urls: string[]) => {
  lightbox.value = urls;
};

const filteredRows = computed(() =>
  activeFilter.value === 'all' ? rows.value : rows.value.filter((r) => r.statusKey === activeFilter.value),
);

const updatedLabel = computed(() => {
  const updated = props.item?.updated_at;
  if (!updated) return null;
  // date-fns Strict has no exact Intl equivalent; keep it for en and use
  // formatRelativeTime/formatDate (Intl-based) for other locales (P6 escape).
  try {
    const d = new Date(updated);
    return locale.value === 'en' ? formatDistanceToNowStrict(d, { addSuffix: true }) : formatRelativeTime(d);
  } catch {
    return locale.value === 'en'
      ? format(new Date(), 'MMM dd')
      : formatDate(new Date(), { day: 'numeric', month: 'short' });
  }
});
</script>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.pp-avail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.stats {
  display: flex;
  gap: 22px;
  flex-wrap: wrap;

  .s {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .l {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 9px;
      letter-spacing: 0.1em;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 600;
      text-transform: uppercase;
    }

    .v {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 22px;
      letter-spacing: -0.02em;
      line-height: 1;

      &.green {
        color: #126b2f;
      }

      &.gold {
        color: #8b6000;
      }

      &.small {
        font-size: 14px;
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-weight: 600;
      }
    }
  }
}

.pp-avail-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;

  .f {
    padding: 7px 12px;
    border-radius: 8px;
    border: 1px solid rgba(13, 15, 21, 0.08);
    background: white;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(13, 15, 21, 0.62);
    cursor: pointer;
    transition: all 0.15s ease;

    &.on {
      background: #0d0f15;
      color: white;
      border-color: #0d0f15;
    }
  }
}

// Horizontal scroll wrapper for narrow viewports — table layout stays
// internally consistent (header / body share the same grid) and the
// browser scroll-bar replaces text wrapping that previously misaligned
// every row.
.pp-avail-table {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  overflow-x: auto;
}

.pp-avail-row {
  display: grid;
  // Fixed widths so the body rows line up perfectly with the header. View
  // gets the only flexible track (capped by minmax so it can't expand to
  // dominate the row, but can't shrink below 120px either). Total ≈ 760px
  // + flex — narrower viewports scroll horizontally instead of wrapping
  // cell content.
  grid-template-columns: 100px 150px minmax(110px, 1.2fr) 60px 90px 130px 80px 110px 110px;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(13, 15, 21, 0.08);
  align-items: center;
  min-width: 1010px;

  &:last-child {
    border-bottom: none;
  }

  // Every cell collapses naturally — prevents min-content from forcing
  // tracks wider than declared and breaking the alignment.
  > * {
    min-width: 0;
  }

  &.h {
    background: #f8f7f7;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 9px;
    letter-spacing: 0.1em;
    color: rgba(13, 15, 21, 0.62);
    font-weight: 700;
    text-transform: uppercase;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .u-link {
    padding: 0;
    background: none;
    border: 0;
    font: inherit;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
  }

  .pp {
    font-size: 12px;
    font-weight: 600;
    color: #0d0f15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .u {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 13px;
    font-weight: 700;
    color: #0d0f15;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .layout {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    .layout-thumb {
      position: relative;
      flex: none;
      width: 48px;
      height: 48px;
      border-radius: 6px;
      overflow: hidden;
      border: 1px solid rgba(13, 15, 21, 0.1);
      padding: 0;
      background: #f3f3f4;
      cursor: pointer;

      img {
        width: 100%;
        height: 100%;
        // Floor plans are line drawings: show the whole plan, never a cropped corner.
        object-fit: contain;
        background: white;
        display: block;
      }

      .layout-count {
        position: absolute;
        inset-inline-end: 2px;
        bottom: 2px;
        min-width: 16px;
        padding: 0 4px;
        border-radius: 8px;
        background: rgba(13, 15, 21, 0.75);
        color: white;
        font-size: 10px;
        font-weight: 700;
        line-height: 16px;
        text-align: center;
      }
    }
    .layout-label {
      font-size: 13px;
      font-weight: 600;
      color: #0d0f15;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .view {
    font-size: 13px;
    color: rgba(13, 15, 21, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .fl {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
    color: #0d0f15;
    font-weight: 600;
    white-space: nowrap;
  }

  .area {
    font-size: 13px;
    color: #0d0f15;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    .sub {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      color: rgba(13, 15, 21, 0.62);
      margin-inline-start: 3px;
      font-weight: 500;
    }
  }

  .price {
    font-size: 14px;
    font-weight: 700;
    color: #0d0f15;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    .cur {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      color: rgba(13, 15, 21, 0.62);
      margin-inline-end: 3px;
      font-weight: 500;
    }
  }

  .st {
    padding: 4px 8px;
    border-radius: 6px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    text-align: center;
    justify-self: start;

    &.avail {
      background: #e8f9e2;
      color: #126b2f;
    }

    &.hold {
      background: #fff6e7;
      color: #8b6000;
    }

    &.sold {
      background: #f3f3f3;
      color: rgba(13, 15, 21, 0.4);
    }

    &.excl {
      background: #0d0f15;
      color: #ffb648;
    }

    &.resv,
    &.book {
      background: rgba(57, 87, 211, 0.14);
      color: #3957d3;
    }
  }

  .book {
    padding: 7px 12px;
    border-radius: 8px;
    background: #0d0f15;
    color: white;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    border: none;
    cursor: pointer;

    &.dis {
      background: #f3f3f3;
      color: rgba(13, 15, 21, 0.4);
      cursor: default;
    }

    &.gold {
      background: #ffb648;
      color: #0d0f15;
    }
  }

  &.sold .u,
  &.sold .layout,
  &.sold .view,
  &.sold .fl,
  &.sold .area,
  &.sold .price {
    opacity: 0.5;
  }
}

.empty {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  padding: 40px;
  text-align: center;
  color: rgba(13, 15, 21, 0.62);
  font-size: 14px;
}
</style>
